import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { Radio } from 'app/models/radio';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ExtractFormControl } from 'app/utils/validator.utils';
import { ChartDisplayOptions } from '../../../../models';
import {
  Decimals,
  PlotType,
  PredictorView,
  StepValue,
  WidgetPredictiveOptions,
} from '../widget-predictive/widget-predictive.component';
import { getRadios } from './predictive-chart-options-dialog.utils';

interface PredictiveOptionForm {
  stepValue: FormControl<StepValue>;
  decimals: FormControl<Decimals>;
  view: FormControl<PredictorView>;
  scatterValue: FormControl<PlotType>;
  displayLabels: FormControl<boolean>;
  showNumericAxisLabels: FormControl<boolean>;
}

type PredictiveOptionFormValue = ExtractFormControl<PredictiveOptionForm>;

@EnableClassTelemetry()
@Component({
  selector: 'app-predictive-chart-options-dialog',
  templateUrl: './predictive-chart-options-dialog.component.html',
  styleUrls: ['./predictive-chart-options-dialog.component.scss'],
  standalone: false,
})
export class PredictiveChartOptionsDialogComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() data: ChartDisplayOptions;

  @Output() modalClosed = new EventEmitter<ChartDisplayOptions>();

  formGroup: FormGroup<PredictiveOptionForm>;

  stepValueRadios: Radio<StepValue>[];
  decimalsRadios: Radio<Decimals>[];
  viewRadios: Radio<PredictorView>[];
  scatterValueRadios: Radio<PlotType>[];

  private readonly formBuilder = inject(FormBuilder);

  ngOnInit() {
    this.buildForm();

    this.initRadios();
  }

  onCancel() {
    this.modalClosed.emit(null);
  }

  onSave() {
    const result = this.getResult();
    this.modalClosed.emit(result);
  }

  private buildForm(): void {
    const payload = <WidgetPredictiveOptions>this.data.payload;

    this.formGroup = this.formBuilder.group<PredictiveOptionForm>({
      stepValue: new FormControl(payload.stepValue, Validators.required),
      decimals: new FormControl(payload.decimals, Validators.required),
      view: new FormControl(payload.view, Validators.required),
      scatterValue: new FormControl(payload.scatterValue, Validators.required),
      displayLabels: new FormControl(payload.displayLabels, Validators.required),
      showNumericAxisLabels: new FormControl(payload.showNumericAxisLabels, Validators.required),
    });
  }

  private initRadios(): void {
    const radios = getRadios();

    this.stepValueRadios = radios.stepValueRadios;
    this.decimalsRadios = radios.decimalsRadios;
    this.viewRadios = radios.viewRadios;
    this.scatterValueRadios = radios.scatterValueRadios;
  }

  private getResult(): ChartDisplayOptions {
    this.data.needRefresh = true;

    const formValue = this.formGroup.getRawValue();

    if (this.data.payload.stepValue !== formValue.stepValue) {
      this.data.payload.simulations = [];
    }

    PredictiveChartOptionsDialogComponent.mapFormToPayload(
      <WidgetPredictiveOptions>this.data.payload,
      formValue
    );

    return this.data;
  }

  private static mapFormToPayload(
    payload: WidgetPredictiveOptions,
    formValue: PredictiveOptionFormValue
  ): void {
    payload.stepValue = formValue.stepValue;
    payload.decimals = formValue.decimals;
    payload.view = formValue.view;
    payload.scatterValue = formValue.scatterValue;
    payload.displayLabels = formValue.displayLabels;
    payload.showNumericAxisLabels = formValue.showNumericAxisLabels;
  }
}
